import { excessAlfa } from '@novaquantis/nq-finance';
import { PerformanceData } from '@shared/SharedAccountBody';
import { RetailAccountContent } from '@shared/types';

/** Align with backend PORTFOLIO_VALUE_FLOOR in alpacaHistory.ts */
export const PORTFOLIO_VALUE_FLOOR = 0.01;

export const isAccountUnfunded = (content?: RetailAccountContent): boolean =>
  content !== undefined && (content.totalValue ?? 0) <= PORTFOLIO_VALUE_FLOOR;

export const normalizePercent = (value?: number) => {
  if (value === undefined || value === null) return 0;
  return Math.abs(value) <= 1 ? value * 100 : value;
};

/** Coerce a possibly-percent value (e.g. 0.19 or 19) to a ratio (0.0019 / 0.19). */
const toRatio = (value?: number): number | undefined => {
  if (value === undefined || value === null) return undefined;
  return Math.abs(value) > 1 ? value / 100 : value;
};

/**
 * Today's excess alpha (portfolio return − SPY return over the same day), as a
 * ratio. Prefers the canonical excessAlfa(portfolioReturn, benchmarkReturn).
 *
 * The TradeEngine only enriches the *since-start* bucket with benchmark data, so
 * `today.benchmarkReturn` is usually absent. Callers can pass the day's SPY return
 * (e.g. derived from the chart's benchmark series) via `benchmarkReturnOverride`.
 * Falls back to the backend `today.alfa` when no benchmark return is available.
 */
export const computeDailyAlpha = (
  performance?: PerformanceData,
  benchmarkReturnOverride?: number,
): number | undefined => {
  const today = performance?.today;
  if (!today) return undefined;
  const portfolioReturn = toRatio(today.percent);
  const benchmarkReturn = toRatio(today.benchmarkReturn ?? benchmarkReturnOverride);
  if (portfolioReturn !== undefined && benchmarkReturn !== undefined) {
    return excessAlfa(portfolioReturn, benchmarkReturn);
  }
  return toRatio(today.alfa);
};

export const buildPerformance = (content?: RetailAccountContent): PerformanceData | undefined => {
  if (!content) return undefined;
  if (content.performance) return content.performance;
  if (content.profitSinceStart !== undefined) {
    return {
      sinceStart: {
        amount: content.profitSinceStart ?? 0,
        percent: (content.profitPercentage ?? 0) / 100,
      },
    };
  }
  return undefined;
};

/** Principal at tradingStartedAt derived from current value and since-start return. */
export const deriveInitialPrincipal = (
  content?: RetailAccountContent,
): number | undefined => {
  if (!content?.totalValue || content.totalValue <= 0) return undefined;

  const raw = content.performance?.sinceStart?.percent;
  if (raw === undefined || raw === null) return undefined;

  const pct = Math.abs(raw) <= 1 ? raw : raw / 100;
  if (pct <= -1) return undefined;

  return Math.round((content.totalValue / (1 + pct)) * 100) / 100;
};

export const formatPctPill = (value?: number) => {
  const pct = normalizePercent(value);
  const sign = pct >= 0 ? '+' : '';
  return `${sign}${pct.toFixed(2)}%`;
};

/** Format a value already in percentage points (e.g. 0.65 → "+0.65%"). */
export const formatPctPoints = (value?: number) => {
  if (value === undefined || value === null) return '—';
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
};

export const pctChangeSince = (
  current: number,
  start: number,
): number | undefined => {
  if (!Number.isFinite(current) || !Number.isFinite(start) || start <= 0) {
    return undefined;
  }
  return ((current / start) - 1) * 100;
};

/** Alpaca may return closed symbols with qty 0; exclude from open holdings. */
export const isOpenPosition = (position: { qty?: number }): boolean =>
  (position.qty ?? 0) > 0;

export const filterOpenPositions = <T extends { qty?: number }>(positions: T[]): T[] =>
  positions.filter(isOpenPosition);

export const computePositionTotals = (content?: RetailAccountContent) => {
  const positions = filterOpenPositions(content?.positions ?? []);
  const invested =
    content?.stockValue ??
    positions.reduce((sum, p) => sum + (p.marketValue ?? 0), 0);

  const totalPL = positions.reduce((sum, p) => sum + (p.unrealizedPL ?? 0), 0);
  const totalCostBasis = positions.reduce((sum, p) => {
    if (p.marketValue !== undefined && p.unrealizedPL !== undefined) {
      return sum + (p.marketValue - p.unrealizedPL);
    }
    return sum;
  }, 0);

  return {
    positionCount: positions.length,
    investedTotal: invested,
    totalUnrealizedPL: totalPL,
    totalUnrealizedPLPct: totalCostBasis > 0 ? totalPL / totalCostBasis : undefined,
  };
};
